import React from 'react';

export type LoginMethod = 'phone' | 'email' | 'emailAndPhone';

export interface LoginMethodOption {
  value: LoginMethod;
  label: string;
  description?: string;
}

const LOGIN_METHOD_OPTIONS: LoginMethodOption[] = [
  {
    value: 'phone',
    label: 'Phone',
    description: 'Users log in with their phone number',
  },
  {
    value: 'email',
    label: 'Email',
    description: 'Users log in with their email address',
  },
  {
    value: 'emailAndPhone',
    label: 'Email and Phone',
    description: 'Users can log in with either email or phone',
  },
];

export interface LoginMethodRadioGroupProps {
  value?: LoginMethod | string;
  onChange: (value: LoginMethod) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  description?: string;
  errorMessage?: string;
  'data-testid'?: string;
}

export const LoginMethodRadioGroup: React.FC<LoginMethodRadioGroupProps> = ({
  value,
  onChange,
  isDisabled = false,
  isRequired = false,
  label = 'Login Method',
  description = 'Choose how users will authenticate',
  errorMessage,
  'data-testid': dataTestId,
}) => {
  const handleChange = (selectedValue: string) => {
    if (!isDisabled) {
      onChange(selectedValue as LoginMethod);
    }
  };

  return (
    <div className="space-y-3" data-testid={dataTestId}>
      <div className="space-y-1">
        <label className="text-sm font-medium text-foreground">
          {label}
          {isRequired && <span className="text-danger ml-1">*</span>}
        </label>
        {description && (
          <p className="text-sm text-default-500">{description}</p>
        )}
      </div>

      <div className="space-y-3" role="radiogroup" aria-label={label}>
        {LOGIN_METHOD_OPTIONS.map((option) => {
          const isSelected = value === option.value;
          return (
            <label
              key={option.value}
              className={`
                flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all
                ${
                  isSelected
                    ? 'border-primary bg-primary-50'
                    : 'border-default-200 bg-content1 hover:bg-content2 hover:border-default-300'
                }
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              onClick={() => handleChange(option.value)}
            >
              {/* Radio Button */}
              <div className="relative">
                <input
                  type="radio"
                  name="loginMethod"
                  value={option.value}
                  checked={isSelected}
                  onChange={() => handleChange(option.value)}
                  disabled={isDisabled}
                  className="sr-only"
                  aria-describedby={`${option.value}-description`}
                />
                <div
                  className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                  ${
                    isSelected
                      ? 'border-primary bg-primary'
                      : 'border-default-300 bg-white'
                  }
                `}
                >
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="font-medium text-foreground">
                  {option.label}
                </div>
                {option.description && (
                  <div
                    id={`${option.value}-description`}
                    className="text-sm text-default-500 mt-1"
                  >
                    {option.description}
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {/* Error Message */}
      {errorMessage && <p className="text-sm text-danger">{errorMessage}</p>}
    </div>
  );
};
